import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';

import { cn } from '@/_shared/lib/utils';

type RawGroupProps = React.ComponentProps<typeof Group> & { direction?: 'horizontal' | 'vertical' };
type PanelGroupProps = RawGroupProps & {
  /** Persist the split layout under this key (localStorage), restoring it on the next mount. */
  autoSaveId?: string;
};

/**
 * `autoSaveId` was a built-in prop in react-resizable-panels v2/v3 but was removed in v4; the
 * supported replacement is the `useDefaultLayout` hook. Only the persistent variant calls it — the
 * key is a static string per call site, so this branch never changes hook order for a given group.
 */
function ResizablePanelGroup({ autoSaveId, ...props }: PanelGroupProps): React.ReactElement {
  return autoSaveId ? <PersistentPanelGroup autoSaveId={autoSaveId} {...props} /> : <RawPanelGroup {...props} />;
}

function PersistentPanelGroup({ autoSaveId, ...props }: RawGroupProps & { autoSaveId: string }): React.ReactElement {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: autoSaveId,
    // No storage during SSR (no window) → the hook no-ops and panels fall back to their defaultSize.
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    // Persist only user drags, NOT programmatic layout commits — otherwise a caller's imperative
    // collapse() (e.g. the dashboard slide shells closing a panel to 0) would be saved and restored,
    // reopening the panel collapsed instead of at the user's remembered width.
    onlySaveAfterUserInteractions: true,
  });
  return <RawPanelGroup id={autoSaveId} defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged} {...props} />;
}

function RawPanelGroup({ className, direction = 'horizontal', ...props }: RawGroupProps): React.ReactElement {
  return (
    <Group
      orientation={direction}
      className={cn('flex h-full w-full data-[orientation=vertical]:flex-col', className)}
      {...props}
    />
  );
}

function ResizablePanel({ className, ...props }: React.ComponentProps<typeof Panel>): React.ReactElement {
  return <Panel className={cn('min-h-0', className)} {...props} />;
}

function ResizableHandle({
  className,
  withHandle = false,
  'aria-label': ariaLabel = 'Resize panels',
  ...props
}: React.ComponentProps<typeof Separator> & { withHandle?: boolean }): React.ReactElement {
  return (
    <Separator
      aria-label={ariaLabel}
      className={cn(
        'group/handle relative flex w-0.5 items-center justify-center bg-border transition-colors',
        'data-[separator=active]:bg-grey-200 data-[separator=hover]:bg-grey-200',
        'after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
        // Stacked panels: react-resizable-panels reports aria-orientation="horizontal" (NOT data-orientation,
        // which it never sets — the old selectors were dead). Flip the line to a full-width thin bar and
        // re-orient the hit area.
        'aria-[orientation=horizontal]:h-0.5 aria-[orientation=horizontal]:w-full',
        // `after:w-auto` overrides the base `after:w-1`, else left:0+right:0+width:0.25rem is over-constrained and the hit area stays a narrow sliver.
        'aria-[orientation=horizontal]:after:inset-x-0 aria-[orientation=horizontal]:after:inset-y-auto aria-[orientation=horizontal]:after:top-1/2 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-auto aria-[orientation=horizontal]:after:translate-x-0 aria-[orientation=horizontal]:after:-translate-y-1/2',
        className,
      )}
      {...props}
    >
      {withHandle ? (
        <div
          className={cn(
            'z-10 flex w-3 items-center justify-center rounded-full border border-border bg-muted px-px py-2 transition-colors',
            'group-data-[separator=active]/handle:border-grey-200 group-data-[separator=hover]/handle:border-grey-200',
            // Stacked panels: make the grip pill wide + short instead of tall + narrow.
            'group-aria-[orientation=horizontal]/handle:h-3 group-aria-[orientation=horizontal]/handle:w-auto group-aria-[orientation=horizontal]/handle:px-2 group-aria-[orientation=horizontal]/handle:py-px',
          )}
        >
          <div
            className={cn(
              'h-14 w-0.5 shrink-0 bg-grey-100 transition-colors',
              'group-data-[separator=active]/handle:bg-grey-200 group-data-[separator=hover]/handle:bg-grey-200',
              // Stacked panels: the grip bar runs horizontally.
              'group-aria-[orientation=horizontal]/handle:h-0.5 group-aria-[orientation=horizontal]/handle:w-14',
            )}
          />
        </div>
      ) : null}
    </Separator>
  );
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup };
